import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import {
  RotateCw,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Check,
  X,
  Move,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

interface Filters {
  brightness: number; // 0–200, 100 = neutral
  contrast: number;
  saturation: number;
}

const DEFAULT_FILTERS: Filters = { brightness: 100, contrast: 100, saturation: 100 };

const CANVAS_SIZE = 480;
const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

interface ImageEditorProps {
  open: boolean;
  src: string | null;
  /** Output aspect (width / height). `null` keeps source ratio. */
  aspect?: number | null;
  /** Output mime type for the produced Blob. */
  outputType?: 'image/png' | 'image/jpeg' | 'image/webp';
  filename?: string;
  onClose: () => void;
  onSave: (file: File) => void;
}

/**
 * Lightweight image editor — crop (pan + zoom inside a fixed-aspect frame),
 * rotate ±90°, and three baseline filters (brightness / contrast / saturation).
 *
 * The canvas itself defines the crop window: pixels outside `CANVAS_SIZE × CANVAS_SIZE/aspect`
 * are discarded on export. The user controls *which* part of the image lands
 * inside that window via:
 *   - drag (pointer events, screen-space pan)
 *   - wheel / pinch (zoom)
 *   - zoom buttons (±10%)
 *   - rotate buttons (±90°)
 *
 * A rule-of-thirds overlay sits above the canvas as a separate DOM layer so it
 * never bleeds into the exported pixels.
 */
export function ImageEditor({
  open,
  src,
  aspect = 1,
  outputType = 'image/webp',
  filename = 'image.webp',
  onClose,
  onSave,
}: ImageEditorProps) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0); // degrees, multiples of 90
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // pan in canvas px
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [imgReady, setImgReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{
    pointerX: number;
    pointerY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const canvasWidth = CANVAS_SIZE;
  const canvasHeight = aspect ? CANVAS_SIZE / aspect : CANVAS_SIZE;

  // Reset state when the dialog opens with a new source.
  useEffect(() => {
    if (!open) return;
    setScale(1);
    setRotate(0);
    setOffset({ x: 0, y: 0 });
    setFilters(DEFAULT_FILTERS);
    setImgReady(false);
    setLoadError(null);
    if (!src) return;
    const img = new Image();
    // crossOrigin must be set BEFORE assigning src. We also surface load
    // failures via setLoadError so the modal shows a readable message instead
    // of silently hanging on a black canvas (the previous behavior — any 404
    // / CORS rejection just never fired onload).
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImgReady(true);
      setLoadError(null);
    };
    img.onerror = () => {
      imgRef.current = null;
      setImgReady(false);
      setLoadError(
        'Could not load the image. The file may be missing or the server denied the cross-origin request.',
      );
    };
    img.src = src;
  }, [open, src]);

  /**
   * Compute how far the image can be panned before its edges retreat past
   * the canvas border. Returns [maxAbsX, maxAbsY]; pan is clamped to ±this.
   * When the image is smaller than the canvas in either axis the limit is 0
   * (any pan would expose background).
   */
  const panLimits = useCallback((): [number, number] => {
    const img = imgRef.current;
    if (!img) return [0, 0];
    const rotated = rotate % 180 !== 0;
    const baseW = rotated ? img.height : img.width;
    const baseH = rotated ? img.width : img.height;
    const cover = Math.max(canvasWidth / baseW, canvasHeight / baseH) * scale;
    // The rotated bounding box dimensions in screen space:
    const drawnW = baseW * cover;
    const drawnH = baseH * cover;
    return [
      Math.max(0, (drawnW - canvasWidth) / 2),
      Math.max(0, (drawnH - canvasHeight) / 2),
    ];
  }, [canvasHeight, canvasWidth, rotate, scale]);

  // Clamp the current offset whenever scale or rotate change, so we never
  // leave a band of empty canvas visible.
  useEffect(() => {
    if (!imgReady) return;
    const [mx, my] = panLimits();
    setOffset((o) => ({
      x: Math.max(-mx, Math.min(mx, o.x)),
      y: Math.max(-my, Math.min(my, o.y)),
    }));
  }, [imgReady, panLimits]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    ctx.save();
    ctx.filter = `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%)`;
    // Pan first (screen-space) so dragging always moves the image where the
    // pointer goes regardless of rotation.
    ctx.translate(canvasWidth / 2 + offset.x, canvasHeight / 2 + offset.y);
    ctx.rotate((rotate * Math.PI) / 180);

    const rotated = rotate % 180 !== 0;
    const baseW = rotated ? img.height : img.width;
    const baseH = rotated ? img.width : img.height;
    const cover = Math.max(canvasWidth / baseW, canvasHeight / baseH) * scale;
    const drawW = img.width * cover;
    const drawH = img.height * cover;
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }, [canvasHeight, canvasWidth, filters, offset.x, offset.y, rotate, scale]);

  useEffect(() => {
    if (imgReady) draw();
  }, [imgReady, draw]);

  // ── Pointer drag → pan ────────────────────────────────────────────
  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!imgReady) return;
    dragStart.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!dragging || !dragStart.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Map screen pixels → canvas pixels (canvas is rendered scaled-to-fit).
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const dx = (e.clientX - dragStart.current.pointerX) * sx;
    const dy = (e.clientY - dragStart.current.pointerY) * sy;
    const [mx, my] = panLimits();
    setOffset({
      x: Math.max(-mx, Math.min(mx, dragStart.current.offsetX + dx)),
      y: Math.max(-my, Math.min(my, dragStart.current.offsetY + dy)),
    });
  };
  const endDrag = () => {
    setDragging(false);
    dragStart.current = null;
  };

  // ── Wheel → zoom ─────────────────────────────────────────────────
  const onWheel = (e: ReactWheelEvent<HTMLCanvasElement>) => {
    if (!imgReady) return;
    // We don't preventDefault here (passive listener) — the parent dialog
    // doesn't scroll, so this is harmless.
    const delta = e.deltaY < 0 ? 0.08 : -0.08;
    setScale((s) => clamp(s + delta, MIN_SCALE, MAX_SCALE));
  };

  const save = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], filename, { type: outputType });
        onSave(file);
      },
      outputType,
      0.9,
    );
  }, [filename, onSave, outputType]);

  const handleReset = () => {
    setScale(1);
    setRotate(0);
    setOffset({ x: 0, y: 0 });
    setFilters(DEFAULT_FILTERS);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('forms.imageEditor.title')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-[1fr_220px]">
          {/* Canvas + crop overlay */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative w-full">
              <canvas
                ref={canvasRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onWheel={onWheel}
                className={cn(
                  'block w-full rounded-md bg-black/40 object-contain shadow-inner',
                  'touch-none select-none',
                  dragging ? 'cursor-grabbing' : imgReady ? 'cursor-grab' : 'cursor-default',
                )}
              />
              {imgReady && <CropOverlay />}
              {loadError && (
                <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                  <p className="rounded-md bg-[rgb(0_0_0/0.7)] px-3 py-2 text-xs text-[var(--color-error)]">
                    {loadError}
                  </p>
                </div>
              )}
            </div>
            <p className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-fg-muted)]">
              <Move className="h-3 w-3" />
              {t('forms.imageEditor.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              <IconButton
                title={t('forms.imageEditor.zoomOut')}
                onClick={() => setScale((s) => clamp(s - 0.1, MIN_SCALE, MAX_SCALE))}
              >
                <ZoomOut className="h-4 w-4" />
              </IconButton>
              <IconButton
                title={t('forms.imageEditor.zoomIn')}
                onClick={() => setScale((s) => clamp(s + 0.1, MIN_SCALE, MAX_SCALE))}
              >
                <ZoomIn className="h-4 w-4" />
              </IconButton>
              <IconButton title={t('forms.imageEditor.rotateLeft')} onClick={() => setRotate((r) => r - 90)}>
                <RotateCcw className="h-4 w-4" />
              </IconButton>
              <IconButton title={t('forms.imageEditor.rotateRight')} onClick={() => setRotate((r) => r + 90)}>
                <RotateCw className="h-4 w-4" />
              </IconButton>
            </div>

            <FilterSlider
              label={t('forms.imageEditor.zoom')}
              min={MIN_SCALE * 100}
              max={MAX_SCALE * 100}
              value={Math.round(scale * 100)}
              onChange={(v) => setScale(clamp(v / 100, MIN_SCALE, MAX_SCALE))}
              suffix="%"
            />
            <FilterSlider
              label={t('forms.imageEditor.brightness')}
              value={filters.brightness}
              onChange={(v) => setFilters((f) => ({ ...f, brightness: v }))}
            />
            <FilterSlider
              label={t('forms.imageEditor.contrast')}
              value={filters.contrast}
              onChange={(v) => setFilters((f) => ({ ...f, contrast: v }))}
            />
            <FilterSlider
              label={t('forms.imageEditor.saturation')}
              value={filters.saturation}
              onChange={(v) => setFilters((f) => ({ ...f, saturation: v }))}
            />

            <button
              type="button"
              onClick={handleReset}
              className="mt-auto text-xs text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
            >
              {t('forms.imageEditor.reset')}
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            <X className="mr-1.5 h-4 w-4" />
            {t('forms.imageEditor.cancel')}
          </Button>
          <Button type="button" onClick={save} disabled={!imgReady}>
            <Check className="mr-1.5 h-4 w-4" />
            {t('forms.imageEditor.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Rule-of-thirds grid drawn as a DOM overlay (NOT painted on the canvas, so
 * it never ends up in the exported pixels). Pointer events pass through.
 */
function CropOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 rounded-md">
      <div className="absolute inset-0 ring-2 ring-inset ring-white/30" />
      {/* vertical thirds */}
      <div className="absolute inset-y-0 left-1/3 w-px bg-white/20" />
      <div className="absolute inset-y-0 left-2/3 w-px bg-white/20" />
      {/* horizontal thirds */}
      <div className="absolute inset-x-0 top-1/3 h-px bg-white/20" />
      <div className="absolute inset-x-0 top-2/3 h-px bg-white/20" />
      {/* corner markers — give the user a clear sense of the crop bounds */}
      <CornerMark className="left-1 top-1 border-l-2 border-t-2" />
      <CornerMark className="right-1 top-1 border-r-2 border-t-2" />
      <CornerMark className="bottom-1 left-1 border-b-2 border-l-2" />
      <CornerMark className="bottom-1 right-1 border-b-2 border-r-2" />
    </div>
  );
}

function CornerMark({ className }: { className: string }) {
  return <span className={cn('absolute h-3 w-3 border-white/80', className)} />;
}

function IconButton({
  children,
  onClick,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-md',
        'border border-[var(--color-border-default)] bg-black/30',
        'hover:border-[var(--color-accent)] hover:bg-black/50',
        'transition-colors',
      )}
    >
      {children}
    </button>
  );
}

function FilterSlider({
  label,
  value,
  onChange,
  min = 0,
  max = 200,
  suffix = '%',
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-xs text-[var(--color-fg-muted)]">
      <span className="flex justify-between">
        <span>{label}</span>
        <span className="font-mono text-[var(--color-fg-primary)]">
          {value}
          {suffix}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 cursor-pointer appearance-none rounded-full bg-[rgb(255_255_255/0.1)] accent-[var(--color-accent)]"
      />
    </label>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
